import React from "react";

/**
 * What each clicks column actually counts, in the client's own wording.
 *
 * Worth keeping on O→Conv: only a signed-in profile can ever be matched to a
 * transaction, and affiliate networks report late — without that, a `Converted 0`
 * on a recent range reads as "nobody bought" rather than "not reported yet".
 *
 * Shared because the same columns appear in Top businesses and in every
 * Breakdown table; two copies would drift the moment one is reworded.
 */
export const CLICKS_METRICS = {
  opens: {
    label: "Opens",
    hint: "How many times this business was opened.",
  },
  outbounds: {
    label: "Outbound",
    hint:
      "Outbound clicks those opens produced — the taps that send someone on " +
      "to the merchant.",
  },
  openers: {
    label: "Openers",
    hint: "How many different people opened it.",
  },
  pairs: {
    label: "Pairs",
    hint:
      "Distinct app profiles that opened it, and the base for the conversion " +
      "rate.",
  },
  converted: {
    label: "Converted",
    hint:
      "Profiles that went on to a qualifying cashback purchase at this " +
      "business, within the conversion window set above the table.",
  },
  openToOutbound: {
    label: "O→Out",
    hint: "Share of opens that led to at least one outbound click.",
  },
  openToConversion: {
    label: "O→Conv",
    hint:
      "Share of opening profiles that converted. Only signed-in users can be " +
      "matched to a transaction, and affiliate networks report late.",
  },
} as const;

export type ClicksMetric = keyof typeof CLICKS_METRICS;

/** A right-aligned metric column header carrying its own explanation. */
export const ClicksMetricHeader: React.FC<{ metric: ClicksMetric }> = ({
  metric,
}) => (
  <th
    className="text-right font-medium px-4 py-3 cursor-help"
    title={CLICKS_METRICS[metric].hint}
  >
    {CLICKS_METRICS[metric].label}
  </th>
);
